import { useEffect, useMemo, useRef, useState } from 'react';
import { getAuthEpoch } from '@/lib/keepsake/epochs';
import { subscribeContentObservations } from '@/lib/content-lifecycle/events';
import { SYNC_SETTLED_EVENT } from '@/lib/sync-events';
import { getLocalContentEntity } from '@/lib/content-lifecycle/store';
import {
  unconfirmedSaveCandidates,
  useAudioQueueStore,
  type QueueItemMeta,
} from '@/lib/audio-queue-store';

/**
 * The destination a notice asks about: a prayer or a bookmark, by exact id,
 * plus its authored edit time. Receipt/content observations independently
 * invalidate the trace guard even when that time stays identical.
 */
export interface UnconfirmedSaveEntity {
  type: 'prayer' | 'bookmark';
  id: string;
  updatedAt: string;
}

/**
 * D4 (2026-10-18): the one bound, commit-pending audio row of this destination
 * whose save the server has not confirmed, or null. A restored device installs
 * such a row from the registry (`receipt_missing`): the row stays bound to its
 * exact content operation and is never rebound, retried or removed by the
 * client. The destination's surface shows its words read-only
 * (UnconfirmedSaveNotice for a prayer, BookmarkUnconfirmedSaveNotice for a
 * journal entry). The selector matches owner, protocol, disposition, bound
 * state, exact entity type and id, an operation id and a nonblank transcript;
 * a bookmark row never answers for a prayer or for another bookmark.
 *
 * Trace guard: on the original device the same row shape exists for the
 * one-RPC window between the commit and the receipt, while the local entity
 * still holds that operation as `pending` or has already installed it as its
 * envelope. Such a row is a save in flight, not an unconfirmed one, and is
 * hidden; a restored device holds neither record. The guard is re-read when
 * the destination's revision changes (`updatedAt`) or the candidate set changes.
 *
 * Reads only: no write, no retry, no rebind, no removal.
 */
export function useUnconfirmedSave(
  entity: UnconfirmedSaveEntity,
): QueueItemMeta | null {
  const items = useAudioQueueStore((s) => s.items);
  const ownerId = useAudioQueueStore((s) => s.scope?.ownerId);
  const [observationRevision, setObservationRevision] = useState(0);
  const readGeneration = useRef(0);
  const authEpoch = getAuthEpoch();
  // Register before the read effect below. Its initial read closes the
  // mount/subscription gap; hints only request a fresh authoritative read.
  useEffect(() => {
    const reread = () => {
      readGeneration.current += 1;
      setObservationRevision((revision) => revision + 1);
    };
    const unsubscribe = subscribeContentObservations((hint) => {
      if (hint.ownerId === ownerId && (hint.entityType === undefined ||
          (hint.entityType === entity.type && hint.entityId === entity.id))) reread();
    });
    const foreground = () => { if (document.visibilityState === 'visible') reread(); };
    window.addEventListener('pageshow', reread);
    window.addEventListener('focus', reread);
    window.addEventListener(SYNC_SETTLED_EVENT, reread);
    document.addEventListener('visibilitychange', foreground);
    return () => {
      readGeneration.current += 1;
      unsubscribe();
      window.removeEventListener('pageshow', reread);
      window.removeEventListener('focus', reread);
      window.removeEventListener(SYNC_SETTLED_EVENT, reread);
      document.removeEventListener('visibilitychange', foreground);
    };
  }, [ownerId, entity.type, entity.id]);
  const candidates = useMemo(
    () => (ownerId === undefined
      ? []
      : unconfirmedSaveCandidates(items, ownerId, { type: entity.type, id: entity.id })),
    [items, ownerId, entity.type, entity.id],
  );
  // Retain an already validated notice while the SAME identity is re-read.
  // A revision/hint invalidates an in-flight read, not that visible identity.
  // A new owner/epoch/destination/op set stays hidden until its own read lands.
  const candidateOpKey = [...new Set(candidates.map((item) => item.destinationOpId ?? ''))].sort().join(',');
  const guardKey = ownerId !== undefined && candidateOpKey !== ''
    ? `${ownerId}:${authEpoch}:${entity.type}:${entity.id}:${candidateOpKey}`
    : null;
  const [guard, setGuard] = useState<{
    key: string | null;
    hiddenOpIds: ReadonlySet<string> | null;
  }>({ key: null, hiddenOpIds: null });
  useEffect(() => {
    if (guardKey === null || ownerId === undefined) return;
    let cancelled = false;
    const generation = ++readGeneration.current;
    void getLocalContentEntity(ownerId, entity.type, entity.id)
      .then((localEntity) => {
        const hidden = new Set<string>();
        for (const opId of candidateOpKey.split(',')) {
          if (
            localEntity?.pending?.opId === opId ||
            localEntity?.installedEnvelope?.opId === opId
          ) hidden.add(opId);
        }
        return hidden as ReadonlySet<string>;
      })
      // An unreadable entity hides nothing: the words exist on this device and
      // the notice that shows them is read-only.
      .catch(() => new Set<string>() as ReadonlySet<string>)
      .then((hiddenOpIds) => {
        if (!cancelled && generation === readGeneration.current && getAuthEpoch() === authEpoch &&
            useAudioQueueStore.getState().scope?.ownerId === ownerId) {
          setGuard({ key: guardKey, hiddenOpIds });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [guardKey, ownerId, entity.type, entity.id, entity.updatedAt, candidateOpKey, authEpoch, observationRevision]);
  // Invalidate qualification on identity departure, including A→B→A while
  // B's read is held. React retries this component before committing children.
  // Ordinary same-identity rereads deliberately retain the existing notice.
  if (guard.key !== guardKey) {
    setGuard({ key: guardKey, hiddenOpIds: null });
    return null;
  }
  const hiddenOpIds = guard.hiddenOpIds;
  if (guardKey === null || hiddenOpIds === null) return null;
  return candidates.find(
    (item) => !hiddenOpIds.has(item.destinationOpId ?? ''),
  ) ?? null;
}
